import { TemplateType_Slug } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { useTranslation } from '@frontend/i18n';
import { getManualTemplateIndexedValue, useTemplateTypeMap } from '@frontend/integrated-messaging';
import { theme } from '@frontend/theme';
import { DropdownField, FieldProps, Stepper, Text } from '@frontend/design-system';
import { overrideTextStyles, stepValueContainerStyles } from './styles';

type TemplateTypeStepProps = {
  fieldProps: FieldProps<
    {
      type: {
        type: 'dropdown';
        required: boolean;
        value: string;
        validateOnChange: boolean;
      };
    },
    'type'
  >;
  next: () => void;
  previous: () => void;
  isFormModeEdit: boolean;
  availableTemplateTypes: TemplateType_Slug[];
};

export const TemplateTypeStep = ({
  fieldProps,
  next,
  isFormModeEdit,
  previous,
  availableTemplateTypes,
}: TemplateTypeStepProps) => {
  const { t } = useTranslation('templates-settings');
  const templateTypeMap = useTemplateTypeMap();

  return (
    <>
      <Stepper.Title css={{ marginBottom: '14px' }}>{t('Select Template Type')}</Stepper.Title>

      <Stepper.Content css={{ display: 'flex', gap: '20px', flexDirection: 'column' }}>
        {!isFormModeEdit && (
          <Text css={{ color: theme.colors.neutral50 }}>{t('Which template type would you like to create?')}</Text>
        )}
        <DropdownField
          label='Template Type'
          {...fieldProps}
          css={{
            maxWidth: '66%',
          }}
          helperText={t('Required')}
        >
          {availableTemplateTypes.map((slug) => {
            const label = getManualTemplateIndexedValue({ templateType: slug, indexedValues: templateTypeMap });
            if (!label) return null;
            return (
              <DropdownField.Option key={slug} value={slug}>
                {label}
              </DropdownField.Option>
            );
          })}
        </DropdownField>
      </Stepper.Content>

      {!isFormModeEdit && (
        <Stepper.ButtonBarAlternate>
          <Stepper.ButtonAlternate onClick={previous} position='secondary' type='none'>
            {t('Previous')}
          </Stepper.ButtonAlternate>
          <Stepper.ButtonAlternate
            onClick={() => {
              if (!!fieldProps.value) next();
            }}
            disabled={!fieldProps.value}
            position='primary'
            type='none'
          >
            {t('Next')}
          </Stepper.ButtonAlternate>
        </Stepper.ButtonBarAlternate>
      )}
    </>
  );
};

export const TemplateTypeNonStepper = ({
  fieldProps,
  availableTemplateTypes,
}: Pick<TemplateTypeStepProps, 'fieldProps' | 'availableTemplateTypes'>) => {
  const templateTypeMap = useTemplateTypeMap();
  return (
    <DropdownField label='Template Type' {...fieldProps}>
      {availableTemplateTypes.map((slug) => {
        const label = getManualTemplateIndexedValue({ templateType: slug, indexedValues: templateTypeMap });
        if (!label) return null;
        return (
          <DropdownField.Option key={slug} value={slug}>
            {label}
          </DropdownField.Option>
        );
      })}
    </DropdownField>
  );
};

export const TemplateTypeStepCollapsed = ({ fieldProps }: Pick<TemplateTypeStepProps, 'fieldProps'>) => {
  const { t } = useTranslation('templates-settings');
  const templateTypeMap = useTemplateTypeMap();

  return (
    <div css={{ ...stepValueContainerStyles, flexDirection: 'column' }}>
      <Text>{t('Select Template Type')}</Text>
      {fieldProps.value && (
        <Text css={overrideTextStyles}>
          {getManualTemplateIndexedValue({
            templateType: fieldProps.value as TemplateType_Slug,
            indexedValues: templateTypeMap,
          }) ?? fieldProps.value}
        </Text>
      )}
    </div>
  );
};
